import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { t, formatDate, formatTime } from '../i18n';
import { Alert, Button } from './ui';

/**
 * What happened with the professional's patients since they last pressed
 * "Entendido": cancellations and withdrawn requests made from the app
 * (migration 0059), and patients reaching 3 or more cancellations or absences
 * in a row (0061). Newest first, for the last 30 days at most.
 *
 * Each of these also reaches the professional by email; this is the reading
 * inside the app, which showed nothing before.
 */

const whenOf = (instant) =>
  `${formatDate(instant, { weekday: 'short', day: 'numeric', month: 'short' })} ${formatTime(instant)}`;

const PatientCancellationsNotice = () => {
  const [rows, setRows] = useState([]);
  const [repeats, setRepeats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      supabase.rpc('my_patient_cancellations'),
      supabase.rpc('my_repeated_cancellations'),
    ]).then(([cancellations, repeated]) => {
      const loadError = cancellations.error ?? repeated.error;
      if (loadError) setError(loadError.message);
      setRows(cancellations.data ?? []);
      setRepeats(repeated.data ?? []);
    });
  }, []);

  const markSeen = async () => {
    setBusy(true);
    const { error: markError } = await supabase.rpc('mark_patient_cancellations_seen');
    setBusy(false);
    if (markError) { setError(markError.message); return; }
    setRows([]);
    setRepeats([]);
    setError(null);
  };

  const count = rows.length + repeats.length;

  // A check that could not run says so, rather than looking like none happened.
  if (error && !count) {
    return <Alert tone="warning" title={t('home.patientCancellationsFailed')}>{error}</Alert>;
  }
  if (!count) return null;

  return (
    <Alert tone="warning" title={t('home.patientCancellationsTitle', { count })}>
      {error && <p className="mb-2">{error}</p>}
      <ul className="space-y-1.5">
        {repeats.map((row) => (
          <li key={`repeat-${row.treatment_id}`} className="font-medium">
            {t('home.repeatedCancellations', { name: `${row.first_name} ${row.last_name}`, count: row.count })}
          </li>
        ))}
        {rows.map((row) => (
          <li key={row.appointment_id}>
            {t(row.withdrawn ? 'home.patientWithdrew' : 'home.patientCancelled', {
              name: `${row.first_name} ${row.last_name}`,
              when: whenOf(row.starts_at),
            })}
            {!row.withdrawn && ` ${t(row.charged ? 'home.cancellationCharged' : 'home.cancellationNotCharged')}`}
            {row.reason && (
              <span className="block text-xs">{t('home.cancellationReason', { reason: row.reason })}</span>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <Button variant="secondary" onClick={markSeen} loading={busy}>{t('home.cancellationsSeen')}</Button>
      </div>
    </Alert>
  );
};

export default PatientCancellationsNotice;
